<script>
  import { beforeUpdate, afterUpdate, createEventDispatcher, onDestroy, onMount } from 'svelte';
  // noinspection ES6UnusedImports
  import { fade, slide } from 'svelte/transition';
  import {
    addComment,
    listenToChannel,
    listenToChannelIds,
    listenToChat,
    logout,
    onAuthStateChanged,
    playLogin,
  } from '../../Api/api';
  import { firebaseApp } from '../../Store/firebase-store';
  import { getChannelIdFromUrl, getTime, isAfter } from '../../utils';
  import PlayerContainer from '../Video/PlayerContainer.svelte';
  import PlayerControlDialog from '../Video/PlayerControlDialog.svelte';
  import ChatInput from './ChatInput.svelte';
  import { channel } from './chat-store';
  import shortid from 'shortid';
  import Login from '../Login/Login.svelte';
  import { loggedInUser, userHasAlias, userHasApprovedGoodBahviour } from '../../Store/user-store';
  import Settings from './Settings.svelte';
  import { trackEvent } from '../../analytics';
  import { loginStore, activeView } from '../Login/loginStore';

  const dispatch = createEventDispatcher();

  export let id = '';
  export let inputVisible = true;
  export let muteAudio = false;
  export let app;
  export let websocketURL;
  export let headerDarkBackground = false;
  export let basePath = '';
  export let hlsURL;

  // Even if playout is HLS, Chat admin should show WebRTC stream for accurate preview
  export let forceWebRTC = false;

  let scrollContainer;
  let scroller;
  let didStartScroll = false;
  let unsubscribeListenToChat;
  let unsubscribeListenToChannel;
  let unsubscribeListenToChannelId;
  let numberOfMessagesWhenStartedScrolling = 0;
  let numberOfNewMessages = 0;

  let videoContainer;
  let playerContainer;
  let isVideoActive = false;
  let showPlayerControlDialog = false;
  let showUserSettings = false;
  let threadVideoStyle = '';
  let videoContainerBounds;
  let showModerationMsg = false;
  // Only show once
  let didShowModerationMsg = false;
  let tmpMessages = {};

  let urlInText = new RegExp(
    '([a-zA-Z0-9]+://)?([a-zA-Z0-9_]+:[a-zA-Z0-9_]+@)?([a-zA-Z0-9.-]+\\.[A-Za-z]{2,4})(:[0-9]+)?(/.*)?',
  );

  $: showNewMessgesLabel = false;
  $: autoScroll = true;
  $: thread = [];
  $: app && firebaseApp.set(app);
  $: id && listenToNewChannelData(id);
  $: id && listenToNewChatData(id);

  onMount(async () => {
    if (!id) {
      unsubscribeListenToChannelId = await listenToChannelIds((docRef) => {
        if (!docRef.empty) {
          const mappings = docRef.docs.map((d) => ({ ...d.data(), id: d.id }));
          const url = window.location.origin + window.location.pathname;
          id = getChannelIdFromUrl(url, mappings);
          if (!id) {
            channel.set(null);
            thread = [];
          }
        }
      }, $firebaseApp);
    }

    await checkState();

    videoContainerBounds = videoContainer.getBoundingClientRect();

    if (window) {
      // Check if TV2 Auth is ready
      const isReady = (await window?.TV2?.isAuthReady()) ?? false;

      if (isReady) {
        checkLogin();
      }

      // Wait for event from menu
      document.addEventListener('tv2-menu:auth-ready', () => {
        checkLogin();
      });
    }
  });

  const checkState = async () => {
    await onAuthStateChanged(async (user) => {
      loggedInUser.set(user);
      $userHasAlias = user && user.displayName ? true : false;

      if (user && !$userHasApprovedGoodBahviour) {
        userHasApprovedGoodBahviour.set(false);
        loginStore.setActiveView(activeView.CHAT_RULES);
      }
    }, $firebaseApp);
  };

  const checkLogin = async () => {
    if (!$loggedInUser) {
      if (window.TV2 && window.TV2.getLoggedInUser) {
        const user = (await window?.TV2?.getLoggedInUser()) ?? null;

        if (user) {
          await playLogin(basePath, user, $firebaseApp);
          await checkState();
        }
      }
    }
  };

  onDestroy(() => {
    if (unsubscribeListenToChat) {
      unsubscribeListenToChat();
    }
    if (unsubscribeListenToChannel) {
      unsubscribeListenToChannel();
    }
    if (unsubscribeListenToChannelId) {
      unsubscribeListenToChannelId();
    }
    if (playerContainer) {
      playerContainer.cancelVideo();
    }
  });

  beforeUpdate(() => {
    autoScroll =
      scrollContainer &&
      scrollContainer.offsetHeight + scrollContainer.scrollTop > scrollContainer.scrollHeight - 20;
  });

  afterUpdate(() => {
    if (autoScroll && scroller && scrollContainer && thread.length) {
      setTimeout(() => scrollContainer.scrollTo(0, scrollContainer.scrollHeight), 150);
    }
  });

  const onChatInputFocus = () => {
    if ($loggedInUser && !$loggedInUser.displayName) {
      showUserSettings = true;
    }
  };

  const addChatComment = async (chat) => {
    if (chat && $loggedInUser) {
      let tmpId = shortid();
      const newMsg = {
        content: chat,
        created: new Date(),
        userId: $loggedInUser.uid,
        userName: $loggedInUser.displayName,
        temp: true,
        id: tmpId,
        tmpId,
      };
      tmpMessages[tmpId] = newMsg;
      thread = [...thread, newMsg];
      await addComment(
        id,
        {
          content: chat,
          created: $firebaseApp.serverTimestamp,
          userId: $loggedInUser.uid,
          userName: $loggedInUser.displayName,
          tmpId,
        },
        $firebaseApp,
      );
      if (!didShowModerationMsg) {
        didShowModerationMsg = true;
        showModerationMsg = true;
        setTimeout(() => {
          showModerationMsg = false;
        }, 2500);
      }
    }
  };

  const onAddComment = (e) => {
    if (e.detail) {
      addChatComment(e.detail);
      if ($channel) {
        trackEvent({
          action: 'Chat - Submit message',
          label: $channel.id,
          dimensions: {
            chatName: $channel.name,
            chatCategory: $channel.category,
          },
        });
      }
    }
  };

  const listenToNewChannelData = async (channelId) => {
    if (unsubscribeListenToChannel) {
      unsubscribeListenToChannel();
      unsubscribeListenToChannel = null;
    }

    unsubscribeListenToChannel = await listenToChannel(
      channelId,
      (snapShot) => {
        channel.set({ id: snapShot.id, ...snapShot.data() });
      },
      $firebaseApp,
    );
  };

  const listenToNewChatData = async (channelId) => {
    if (unsubscribeListenToChat) {
      unsubscribeListenToChat();
      unsubscribeListenToChat = null;
      numberOfMessagesWhenStartedScrolling = 0;
      numberOfNewMessages = 0;
    }
    unsubscribeListenToChat = await listenToChat(
      channelId,
      (snapShot) => {
        let chatData = [];
        snapShot.forEach((doc) => {
          const data = doc.data();
          if (!doc.metadata.hasPendingWrites || (doc.metadata.hasPendingWrites && data.created)) {
            if (tmpMessages[data.tmpId]) {
              delete tmpMessages[data.tmpId];
            }
            chatData.push({
              ...data,
              created: data.created.toDate(),
              id: doc.id,
            });
          }
        });

        if (numberOfMessagesWhenStartedScrolling !== 0) {
          numberOfNewMessages = chatData.length - numberOfMessagesWhenStartedScrolling;
        } else {
          numberOfNewMessages = 0;
        }
        shouldShowNewMessgesLabel();

        chatData = [...chatData, ...Object.values(tmpMessages)];

        thread = chatData.sort((a, b) => {
          return isAfter(a.created, b.created) ? 1 : -1;
        });
      },
      $firebaseApp,
    );
  };

  const onViewNewMessagesClicked = () => {
    if (scroller && scrollContainer) {
      scrollContainer.scrollTo(0, scrollContainer.scrollHeight);
      autoScroll = true;
      numberOfMessagesWhenStartedScrolling = 0;
      showNewMessgesLabel = false;
      didStartScroll = false;
    }
  };

  const shouldShowNewMessgesLabel = () => {
    if (scroller && scrollContainer) {
      if (scrollContainer.scrollTop === scroller.scrollHeight - scrollContainer.clientHeight) {
        showNewMessgesLabel = false;
        didStartScroll = false;
      } else if (numberOfNewMessages > 0) {
        showNewMessgesLabel = true;
      }
    }
  };

  const onScroll = (e) => {
    if (scrollContainer.scrollTop !== scroller.scrollHeight - scrollContainer.clientHeight) {
      autoScroll = false;
      if (!didStartScroll) {
        numberOfMessagesWhenStartedScrolling = thread.length - Object.keys(tmpMessages).length;
        didStartScroll = true;
      }
    } else {
      autoScroll = true;
      showNewMessgesLabel = false;
      numberOfMessagesWhenStartedScrolling = 0;
      didStartScroll = false;
    }
  };

  const containsLink = (content) => {
    return content.match(urlInText);
  };

  const onVideoReady = (e) => {
    if (e.detail.paused) {
      showPlayerControlDialog = true;
    } else {
      isVideoActive = true;
      threadVideoStyle = `padding-top: ${videoContainerBounds.height / 2}px`;
    }
  };

  const onVideoDone = () => {
    showPlayerControlDialog = false;
    isVideoActive = false;
    threadVideoStyle = '';
  };

  const onVideoPlaying = () => {
    showPlayerControlDialog = false;
    isVideoActive = true;
    threadVideoStyle = `padding-top: ${videoContainerBounds.height / 2}px`;
  };

  const setLink = (content) => {
    let urlLink;
    let preUrl = '';
    const urlArray = content.match(urlInText);
    if (urlArray && urlArray[0]) {
      if (urlArray[0].indexOf('http') === -1) {
        preUrl = '//' + urlArray[0];
        urlLink = urlArray[0].link(preUrl);
      } else {
        urlLink = urlArray[0].link(urlArray[0]);
      }
      return content.replace(
        urlArray[0],
        urlLink.split('<a').join('<a class="text-link" target="_blank"'),
      );
    }
    return content;
  };

  const onPlayerControlDialogOk = () => {
    isVideoActive = true;
    threadVideoStyle = `padding-top: ${videoContainerBounds.height / 2}px`;
    playerContainer.playVideo();
    showPlayerControlDialog = false;
  };

  const onPlayerControlDialogCancel = () => {
    showPlayerControlDialog = false;
    playerContainer.cancelVideo();
  };

  const onWindowResize = () => {
    if (videoContainer) {
      videoContainerBounds = videoContainer.getBoundingClientRect();
      if (isVideoActive) {
        threadVideoStyle = `padding-top: ${videoContainerBounds.height / 2}px`;
      }
    }
  };

  const onUserBadgeClicked = () => {
    if ($channel) {
      if (!showUserSettings) {
        trackEvent({
          action: 'Chat - Edit alias',
          label: $channel.id,
          dimensions: {
            chatName: $channel.name,
            chatCategory: $channel.category,
          },
        });
      }
    }
    showUserSettings = !showUserSettings;
  };

  const onUserSettingsClose = () => {
    showUserSettings = false;
  };

  const onUserIsReady = () => {
    userHasApprovedGoodBahviour.set(true);
  };

  const onUserGotAlias = () => {
    $userHasAlias = true;
    showUserSettings = false;
  };

  const onDidCancelLogin = async () => {
    if ($loggedInUser) {
      await logout($firebaseApp);
    }
  };
</script>

<svelte:window on:resize={onWindowResize} />

<!-- svelte-ignore a11y-click-events-have-key-events -->
<div
  class={`chat-container ${
    $channel && $channel.category ? `chat-container--${$channel.category}` : ''
  }`}
  on:click={dispatch('channelclicked', { channel: $channel })}
>
  <div class="additional-header" class:show-mobile-navigation={$$slots.mobile_navigation}>
    <slot name="mobile_navigation" />
    {#if $loggedInUser && inputVisible}
      <button
        class="user"
        class:light={headerDarkBackground}
        class:dark={!headerDarkBackground}
        on:click={onUserBadgeClicked}
      >
        {#if !showUserSettings}
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.8333 12C14.5947 12 16.8333 9.76142 16.8333 7C16.8333 4.23858 14.5947 2 11.8333 2C9.07189 2 6.83331 4.23858 6.83331 7C6.83331 9.76142 9.07189 12 11.8333 12Z"
              fill="white"
            />
            <path
              d="M11.8333 13.6666C6.20833 13.6666 3.5 17.1766 3.5 19.2216V20.3333C3.5 20.7753 3.67559 21.1992 3.98816 21.5118C4.30072 21.8244 4.72464 22 5.16667 22H18.5C18.942 22 19.3659 21.8244 19.6785 21.5118C19.9911 21.1992 20.1667 20.7753 20.1667 20.3333V19.2216C20.1667 17.1766 17.4583 13.6666 11.8333 13.6666Z"
              fill="white"
            />
          </svg>
        {:else}
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M19 6.99865L17.0013 5L12 10.0013L6.99865 5L5 6.99865L10.0013 12L5 17.0013L6.99865 19L12 13.9987L17.0013 19L19 17.0013L13.9987 12L19 6.99865Z"
              fill="#FAFAFA"
            />
          </svg>
        {/if}
      </button>
    {/if}
  </div>

  {#if isVideoActive}
    <div class="video-chat-input-background" />
  {/if}

  <div class="header">
    {#if $channel}
      <h3
        class:live={($channel.category === 'news' ||
          $channel.category === 'entertainment' ||
          $channel.category === 'sport') &&
          $channel.live}
      >
        {$channel.name}
        {#if $channel.category === 'news' || $channel.category === 'entertainment' || $channel.category === 'sport'}
          {#if $channel.live}
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle opacity="0.2" cx="7" cy="7" r="7" fill="#DC0E2A" />
              <circle cx="7" cy="7" r="3" fill="#DC0E2A" />
            </svg>
          {/if}
        {/if}
      </h3>
      <div class="right-content">
        <div class="live-indicator" class:live={$channel.live}>
          {#if $channel.category !== 'news' && $channel.category !== 'entertainment' && $channel.category !== 'sport'}
            {$channel.live ? 'Live chat' : 'Ikke aktiv'}
          {/if}
        </div>
        {#if $loggedInUser && inputVisible}
          <button class="user" on:click={onUserBadgeClicked}>
            {#if !showUserSettings}
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 3.5C10.8628 3.5 9.75106 3.83723 8.80547 4.46905C7.85989 5.10087 7.1229 5.99889 6.6877 7.04957C6.25249 8.10025 6.13862 9.25638 6.36049 10.3718C6.58235 11.4872 7.12999 12.5117 7.93414 13.3159C8.73829 14.12 9.76284 14.6677 10.8782 14.8895C11.9936 15.1114 13.1498 14.9975 14.2004 14.5623C15.2511 14.1271 16.1491 13.3901 16.781 12.4445C17.4128 11.4989 17.75 10.3872 17.75 9.25C17.75 7.72501 17.1442 6.26247 16.0659 5.18414C14.9875 4.1058 13.525 3.5 12 3.5ZM12 12.125C11.2375 12.125 10.5062 11.8221 9.96707 11.2829C9.4279 10.7438 9.125 10.0125 9.125 9.25H14.875C14.875 10.0125 14.5721 10.7438 14.0329 11.2829C13.4938 11.8221 12.7625 12.125 12 12.125Z"
                  fill="white"
                />
                <path d="M20 17H4V20H20V17Z" fill="white" />
              </svg>
            {:else}
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19 6.99865L17.0013 5L12 10.0013L6.99865 5L5 6.99865L10.0013 12L5 17.0013L6.99865 19L12 13.9987L17.0013 19L19 17.0013L13.9987 12L19 6.99865Z"
                  fill="white"
                />
              </svg>
            {/if}
          </button>
        {/if}
      </div>
    {:else}
      <h3>...</h3>
    {/if}
  </div>

  <div class="chat-main">
    <div class="video-container" bind:this={videoContainer}>
      {#if !!websocketURL}
        <PlayerContainer
          bind:this={playerContainer}
          {id}
          {muteAudio}
          {websocketURL}
          {hlsURL}
          {forceWebRTC}
          on:video-ready={onVideoReady}
          on:video-done={onVideoDone}
          on:video-playing={onVideoPlaying}
        />
      {/if}
      {#if isVideoActive}
        <div class="video-comments-background" />
      {/if}
    </div>
    <div
      class="thread-container"
      class:video-chat={isVideoActive}
      bind:this={scrollContainer}
      on:scroll={onScroll}
    >
      <div class="tread" bind:this={scroller} style={threadVideoStyle}>
        {#each thread as comment (comment.id)}
          <div
            class:comment={!isVideoActive}
            class:inputVisible
            class:inputNotVisible={!inputVisible}
            id={'c_' + comment.id}
            class:video-comment={isVideoActive}
            in:fade={{ delay: 150, duration: 500 }}
          >
            <div class="content">
              {#if comment.originalContent}
                <div class="content-inline" class:moderator={comment.m}>
                  <div class="info" class:tmp={comment.temp}>
                    <span class="user-name">{comment.userName}</span>
                    {#if comment.m}
                      <div class="icon">
                        {#if $channel?.category === 'news' || $channel?.category === 'entertainment' || $channel?.category === 'sport'}
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M8 0C6.41775 0 4.87103 0.469192 3.55544 1.34824C2.23985 2.22729 1.21447 3.47672 0.608967 4.93853C0.00346625 6.40034 -0.15496 8.00887 0.153721 9.56072C0.462403 11.1126 1.22433 12.538 2.34315 13.6569C3.46197 14.7757 4.88743 15.5376 6.43928 15.8463C7.99113 16.155 9.59966 15.9965 11.0615 15.391C12.5233 14.7855 13.7727 13.7602 14.6518 12.4446C15.5308 11.129 16 9.58225 16 8C16 5.87827 15.1571 3.84344 13.6569 2.34315C12.1566 0.842855 10.1217 0 8 0ZM7 12.245L2.94 8.19L4.705 6.42L7 8.71L11.245 4.46L13 6.23L7 12.245Z"
                            />
                          </svg>
                        {:else}
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M14.0442 1.3385L8.04425 0.338498C8.00805 0.33248 7.97111 0.33248 7.93492 0.338498L1.93492 1.3385C1.85708 1.35144 1.78636 1.39157 1.73534 1.45175C1.68431 1.51193 1.65629 1.58826 1.65625 1.66716V8.66716C1.65625 10.6868 2.74158 12.5378 4.79425 14.0208C5.74438 14.7034 6.78419 15.2516 7.88425 15.6498C7.95257 15.6729 8.0266 15.6729 8.09491 15.6498C9.19497 15.2516 10.2348 14.7034 11.1849 14.0208C13.2376 12.5378 14.3229 10.6868 14.3229 8.66716V1.66716C14.3229 1.58826 14.2949 1.51193 14.2438 1.45175C14.1928 1.39157 14.1221 1.35144 14.0442 1.3385ZM7.36058 10.9808L4.38025 8.0005L5.32292 7.05783L7.28525 9.02016L11.2519 4.39383L12.2642 5.2605L7.36058 10.9808Z"
                            />
                          </svg>
                        {/if}
                      </div>
                    {/if}
                    <span class="time">{getTime(comment.created)}</span>
                  </div>
                  {#if containsLink(comment.content)}
                    <div>
                      {@html setLink(comment.content)}
                    </div>
                  {:else}
                    <div>{comment.content}</div>
                  {/if}
                  <div class="orginal-author">
                    {`${comment.originalAuthor} sa:`}
                  </div>
                  <div class="orginal-content">
                    {#if containsLink(comment.originalContent)}
                      {@html setLink(comment.originalContent)}
                    {:else}{comment.originalContent}{/if}
                  </div>
                </div>
              {:else}
                <div
                  class="content-inline"
                  class:moderator={comment.m}
                  class:tmp-comment={comment.temp}
                >
                  <div class="info" class:tmp={comment.temp}>
                    <span class="user-name">{comment.userName}</span>
                    {#if comment.m}
                      <div class="icon">
                        {#if $channel?.category === 'news' || $channel?.category === 'entertainment' || $channel?.category === 'sport'}
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M8 0C6.41775 0 4.87103 0.469192 3.55544 1.34824C2.23985 2.22729 1.21447 3.47672 0.608967 4.93853C0.00346625 6.40034 -0.15496 8.00887 0.153721 9.56072C0.462403 11.1126 1.22433 12.538 2.34315 13.6569C3.46197 14.7757 4.88743 15.5376 6.43928 15.8463C7.99113 16.155 9.59966 15.9965 11.0615 15.391C12.5233 14.7855 13.7727 13.7602 14.6518 12.4446C15.5308 11.129 16 9.58225 16 8C16 5.87827 15.1571 3.84344 13.6569 2.34315C12.1566 0.842855 10.1217 0 8 0ZM7 12.245L2.94 8.19L4.705 6.42L7 8.71L11.245 4.46L13 6.23L7 12.245Z"
                            />
                          </svg>
                        {:else}
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M14.0442 1.3385L8.04425 0.338498C8.00805 0.33248 7.97111 0.33248 7.93492 0.338498L1.93492 1.3385C1.85708 1.35144 1.78636 1.39157 1.73534 1.45175C1.68431 1.51193 1.65629 1.58826 1.65625 1.66716V8.66716C1.65625 10.6868 2.74158 12.5378 4.79425 14.0208C5.74438 14.7034 6.78419 15.2516 7.88425 15.6498C7.95257 15.6729 8.0266 15.6729 8.09491 15.6498C9.19497 15.2516 10.2348 14.7034 11.1849 14.0208C13.2376 12.5378 14.3229 10.6868 14.3229 8.66716V1.66716C14.3229 1.58826 14.2949 1.51193 14.2438 1.45175C14.1928 1.39157 14.1221 1.35144 14.0442 1.3385ZM7.36058 10.9808L4.38025 8.0005L5.32292 7.05783L7.28525 9.02016L11.2519 4.39383L12.2642 5.2605L7.36058 10.9808Z"
                            />
                          </svg>
                        {/if}
                      </div>
                    {/if}
                    <span class="time">{getTime(comment.created)}</span>
                  </div>
                  {#if containsLink(comment.content)}
                    <span>{@html setLink(comment.content)}</span>
                  {:else}
                    <span>{comment.content}</span>
                  {/if}
                </div>
              {/if}
            </div>
          </div>
        {/each}
      </div>
    </div>
    {#if showNewMessgesLabel}
      <button class="new-msg-label" on:click={onViewNewMessagesClicked}>
        <span
          >{`${numberOfNewMessages} ${
            numberOfNewMessages > 1 ? 'nye meldinger' : 'ny melding'
          }`}</span
        >
      </button>
    {/if}
    {#if showModerationMsg}
      <div class="moderation-msg" in:slide out:slide>
        <span>Takk for at du deltar i chatten!</span>
        <span>Vi modererer fortløpende.</span>
      </div>
    {/if}
  </div>
  {#if inputVisible}
    {#if $loggedInUser && $userHasApprovedGoodBahviour}
      <div class="chat-input-background" />
      <div class="chat-input">
        <ChatInput
          channel={$channel}
          on:addcomment={onAddComment}
          on:chatinputfocused={onChatInputFocus}
        />
      </div>
    {:else}
      <div class="chat-input-background" />
      <div class="login">
        <Login
          on:user-is-ready={onUserIsReady}
          {basePath}
          channelId={id}
          on:did-cancel={onDidCancelLogin}
        />
      </div>
    {/if}
  {/if}
  {#if showPlayerControlDialog}
    <div class="player-control-dialog-container">
      <PlayerControlDialog
        on:ok={onPlayerControlDialogOk}
        on:cancel={onPlayerControlDialogCancel}
      />
    </div>
  {/if}
  {#if showUserSettings}
    <div class="user-settings" in:fade>
      <Settings
        on:close={onUserSettingsClose}
        on:user-got-alias={onUserGotAlias}
        user={$loggedInUser}
      />
    </div>
  {/if}
</div>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<style src="./chat.scss">
</style>
